import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  loginApi, setToken, clearToken, getToken, searchWorkers, getMe, setRememberedEmail,
  refreshSessionApi, replaceToken, setSessionEndedHandler,
} from '../services/api';
import { decodeJWT } from '../lib/permissions';

const AuthContext = createContext(null);
const USER_KEY = 'jornada_user';
const TOKEN_KEY = 'jornada_jwt'; // mismo que services/api.js (evento `storage` entre pestañas)

// ── Renovación del token ──────────────────────────────────────────────────
// Los tokens caducan (30 días). Al cargar la web y al recuperar el foco la
// pestaña, si el token tiene más de 7 días (iat) o es legacy (sin exp), se
// pide uno nuevo a POST /api/auth/refresh. Fallos de red / backend antiguo sin
// la ruta → se ignoran (sin reintentar hasta TOKEN_REFRESH_RETRY_MS). Un 401
// lo gestiona api.js (cierra la sesión). MISMA LÓGICA en TemControlApp/src/context/AuthContext.js.
const TOKEN_REFRESH_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const TOKEN_REFRESH_RETRY_MS = 10 * 60 * 1000;

export const tokenNeedsRefresh = (payload, nowMs = Date.now()) => {
  if (!payload) return false;
  if (payload.exp == null) return true; // legacy (backend antiguo)
  const iat = Number(payload.iat);
  if (!Number.isFinite(iat)) return true;
  return nowMs - iat * 1000 > TOKEN_REFRESH_AFTER_MS;
};

const clearCachedUser = () => {
  try {
    localStorage.removeItem(USER_KEY);
    sessionStorage.removeItem(USER_KEY);
  } catch (_) {}
};

const buildUser = (payload, fullWorker = {}) => ({
  id: payload.id ?? fullWorker.id,
  username: payload.email || fullWorker.email || '',
  email: payload.email || fullWorker.email || '',
  nombre: payload.name || fullWorker.name || payload.email || '',
  type: payload.type ?? fullWorker.type ?? 0,
  companyId: payload.companyId || fullWorker.companyId || null,
  // Multicentro: `workingSite` se mantiene como centro PRIMARIO (compat legacy).
  // `workingSites` es el array completo de centros asignados; si el backend aún
  // no lo manda, se deriva de [workingSite] para no romper la lógica existente.
  workingSite: payload.workingSite ?? fullWorker.workingSite ?? null,
  workingSites: (() => {
    const arr = payload.workingSites ?? fullWorker.workingSites;
    if (Array.isArray(arr) && arr.length) return arr.map((s) => String(s));
    const single = payload.workingSite ?? fullWorker.workingSite;
    return single != null ? [String(single)] : [];
  })(),
  primaryWorkplaceId: payload.primaryWorkplaceId ?? fullWorker.primaryWorkplaceId
    ?? payload.workingSite ?? fullWorker.workingSite ?? null,
  canRequestCheckins: fullWorker.canRequestCheckins ?? true,
  overtimeAllowed: fullWorker.overtimeAllowed ?? false,
  geoTracking: fullWorker.geoTracking ?? false,
  // Si está activado, el trabajador no puede picar antes de su hora de
  // entrada (in1 del día). Compatible con overtimeAllowed: si ambos true,
  // las extras solo cuentan después de out, nunca antes de in.
  enforceStartHour: fullWorker.enforceStartHour ?? false,
  // startHour/endHour eliminados: la jornada vive ahora en /api/workhours/:id.
  province: fullWorker.province || null,
  locality: fullWorker.locality || null,
  autonomousCommunity: fullWorker.autonomousCommunity || null,
  agreement: fullWorker.agreement ?? null,
});

// ¿El backend no tiene /api/me (aún sin desplegar)? El antiguo responde a
// rutas desconocidas con 500 (su notFoundHandler), no 404. Red caída o 401/403
// NO cuentan: ahí el fallback fallaría igual.
const isMissingMeEndpoint = (err) => err?.status === 404 || err?.status >= 500;

// Ficha completa del usuario del token. Lanza si falla la red.
// 1) /api/me: solo la propia ficha (sin descargar la lista de personas).
// 2) Fallback para backends sin /api/me: el endpoint /api/company/person/xyz
//    filtra por NOMBRE, así que pasarle el email no devuelve nada. Pedimos la
//    lista entera y filtramos localmente por id (el JWT trae siempre el id) o
//    email como fallback.
const findFullWorker = async (payload) => {
  try {
    const me = await getMe();
    if (me && !Array.isArray(me) && Number(me.id) === Number(payload.id)) return me;
  } catch (err) {
    if (!isMissingMeEndpoint(err)) throw err;
  }
  const workers = await searchWorkers('');
  if (!Array.isArray(workers)) return null;
  return workers.find((w) => w.id === payload.id) ||
         workers.find((w) => (w.email || '').toLowerCase() === (payload.email || '').toLowerCase()) ||
         null;
};

const hydrateFromToken = async (token) => {
  const payload = decodeJWT(token);
  if (!payload) return null;
  let fullWorker = null;
  try {
    fullWorker = await findFullWorker(payload);
  } catch (_) {}
  return buildUser(payload, fullWorker || {});
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Mensaje para /login tras un cierre forzado (sesión caducada / acceso deshabilitado).
  const [sessionMessage, setSessionMessage] = useState(null);
  const tokenRefreshingRef = useRef(null);
  const lastTokenRefreshTryRef = useRef(0);

  // api.js avisa aquí ante un 401 (una sola vez por sesión). Con user = null
  // las rutas protegidas redirigen a /login. Registrado ANTES de la carga
  // inicial: si el token ya está muerto, el primer /api/me dispara el cierre.
  useEffect(() => {
    setSessionEndedHandler(({ message }) => {
      setSessionMessage(message);
      setUser(null);
      clearCachedUser();
    });
    return () => setSessionEndedHandler(null);
  }, []);

  // Otra pestaña cerró sesión (o la cerró un 401 allí) → aquí también.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === TOKEN_KEY && !e.newValue && !getToken()) setUser(null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Renueva el token si toca. Tras renovar, rehidrata el usuario (rol/empresa
  // actuales vienen en el token nuevo).
  const refreshSessionIfNeeded = useCallback(async () => {
    if (tokenRefreshingRef.current) return tokenRefreshingRef.current;
    const run = (async () => {
      try {
        const token = getToken();
        if (!token) return false;
        const payload = decodeJWT(token);
        if (!tokenNeedsRefresh(payload)) return false;
        if (Date.now() - lastTokenRefreshTryRef.current < TOKEN_REFRESH_RETRY_MS) return false;
        lastTokenRefreshTryRef.current = Date.now();
        const res = await refreshSessionApi();
        const next = res?.token;
        const nextPayload = next ? decodeJWT(next) : null;
        if (!nextPayload || Number(nextPayload.id) !== Number(payload.id)) return false;
        // Logout / cambio de cuenta mientras tanto → no pisamos nada.
        if (getToken() !== token) return false;
        replaceToken(next);
        const u = await hydrateFromToken(next);
        if (u && getToken() === next) {
          setUser((prev) => (prev && prev.id === u.id ? u : prev));
        }
        return true;
      } catch (_) {
        return false; // red, backend antiguo (404/500)… silencio
      } finally {
        tokenRefreshingRef.current = null;
      }
    })();
    tokenRefreshingRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    (async () => {
      const token = getToken();
      if (!token) { setLoading(false); return; }
      // Hidratacion tolerante: si decodeJWT devuelve null, el token esta
      // realmente roto y lo limpiamos. Pero si lo unico que falla es la
      // llamada a searchWorkers (red caida, server lento), buildUser usa
      // los campos del payload del JWT y la sesion sigue activa. Ya no
      // forzamos al usuario a re-loguearse por blips de red.
      // Si el backend respondio 401 (token caducado/usuario deshabilitado),
      // api.js ya limpio el token y aviso: no resucitamos al usuario.
      const u = await hydrateFromToken(token);
      if (u && getToken() === token) {
        setUser(u);
        refreshSessionIfNeeded();
      } else if (!u) clearToken();
      setLoading(false);
    })();
  }, [refreshSessionIfNeeded]);

  // Pestaña recupera el foco → renovación si toca (barato: solo decodifica).
  useEffect(() => {
    if (!user?.id) return undefined;
    const onFocus = () => { refreshSessionIfNeeded(); };
    const onVisible = () => { if (document.visibilityState === 'visible') refreshSessionIfNeeded(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.id, refreshSessionIfNeeded]);

  const clearSessionMessage = useCallback(() => setSessionMessage(null), []);

  // login(email, password, remember)
  //   · remember=true (por defecto)  → token a localStorage. La sesion
  //     persiste indefinidamente al cerrar y reabrir el navegador.
  //   · remember=false               → token a sessionStorage. Al cerrar
  //     la pestana o el navegador, la sesion se pierde.
  const login = async (email, password, remember = true) => {
    if (!email || !password) throw new Error('Introduce usuario y contraseña.');
    setSessionMessage(null);

    const res = await loginApi(email, password);
    const token = res?.token || res?.accessToken || (typeof res === 'string' ? res : null);
    if (!token) throw new Error('El servidor no devolvió un token de sesión.');

    setToken(token, remember);
    // Pre-rellenado del email la proxima vez que abra el login.
    setRememberedEmail(remember ? email : '');

    const u = await hydrateFromToken(token);
    if (!u) {
      clearToken();
      throw new Error('No se pudo leer el token de sesión.');
    }
    // 401 durante la hidratación → api.js ya cerró la sesión y dejó el mensaje
    // (LoginPage navega a /panel, ProtectedRoute devuelve a /login con el aviso).
    if (getToken() !== token) return { user: null };
    setUser(u);
    try {
      // Cache del user del lado cliente. Mismo storage que el token.
      const userJson = JSON.stringify(u);
      if (remember) {
        sessionStorage.removeItem(USER_KEY);
        localStorage.setItem(USER_KEY, userJson);
      } else {
        localStorage.removeItem(USER_KEY);
        sessionStorage.setItem(USER_KEY, userJson);
      }
    } catch (_) {}
    return { user: u };
  };

  const logout = () => {
    setUser(null);
    setSessionMessage(null);
    clearToken();
    clearCachedUser();
    // Mantenemos setRememberedEmail intacto: aunque cierre sesion, si
    // habia activado "Recuerdame" el email pre-rellena de nuevo.
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, sessionMessage, clearSessionMessage }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};
